import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useAuth } from "hooks/useAuth";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const INITIAL_STATE = {
  account: "",
  entity: "",
  vehicle: null,
  driver: null,
  isActive: true,
  sourceBranch: null,
  destinationBranch: null,
  routes: [{ route: null, startStop: null, endStop: null }],
};

const TripForm = ({ isBusy = false, tripDetail = null, onSubmit = () => { } }) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] = useLazyGetDDLDriverListQuery();
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] = useLazyGetDDLBranchListQuery();
  const [fetchRoutes, { data: routes, isLoading: loadingRoutes }] = useLazyGetDDLRouteShipmentsQuery();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
      vehicle: tripDetail?.vehicle || null,
      driver: tripDetail?.driver || null,
      isActive: tripDetail?.isActive ?? true,
      sourceBranch: tripDetail?.sourceBranch || null,
      destinationBranch: tripDetail?.destinationBranch || null,
      routes: tripDetail?.routes || [{ route: null, startStop: null, endStop: null }],
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchDriver = useWatch({ control, name: "driver" });
  const _watchRoutes = useWatch({ control, name: "routes" });
  const _watchSourceBranch = useWatch({ control, name: "sourceBranch" });
  const _watchDestinationBranch = useWatch({ control, name: "destinationBranch" });
  const _watchVehicle = useWatch({ control, name: "vehicle" });

  const selectedVehicle = _watchDriver?.assigned_vehicle_ids?.length
    ? _watchDriver.assigned_vehicle_ids
    : [];

  // Validation function for source branch and first start stop
  const validateSourceBranchMatch = () => {
    if (!_watchSourceBranch || !_watchRoutes?.[0]?.startStop) return true;

    const sourceBranchCode = _watchSourceBranch.code;
    const firstStartStopOrigin = _watchRoutes[0].startStop?.stopOrigin?.code;

    if (sourceBranchCode !== firstStartStopOrigin) {
      toast.error("Source branch must match the first route's start stop origin.");
      return false;
    }
    return true;
  };

  const handleOnSubmit = (data) => {
    // Validate source branch matches first start stop
    if (!validateSourceBranchMatch()) {
      return;
    }

    // Validate destination branch matches last end stop
    if (_watchRoutes?.length > 0) {
      const lastEndStop = _watchRoutes[_watchRoutes.length - 1]?.endStop?.stopDestination?.code;
      if (_watchDestinationBranch?.code && lastEndStop !== _watchDestinationBranch.code) {
        toast.error("End stop must match destination branch");
        return;
      }
    }

    // Proceed with form submission
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      vehicle: data?.vehicle?._id,
      driver: data?.driver?._id,
      sourceBranch: data?.sourceBranch?._id || null,
      destinationBranch: data?.destinationBranch?._id || null,
      routes: data?.routes?.map((r) => ({
        route: r.route?._id,
        startStop: r.startStop?.stopOrigin?._id,
        endStop: r.endStop?.stopDestination?._id,
      })) || [],
    };
    onSubmit(formattedData);
  };

  const addRouteField = () => {
    setValue("routes", [...(_watchRoutes || []), { route: null, startStop: null, endStop: null }]);
  };

  const removeRouteField = () => {
    if (_watchRoutes && _watchRoutes.length > 1) {
      setValue("routes", _watchRoutes.slice(0, -1));
    }
  };

  React.useEffect(() => {
    if (!tripDetail) return;

    const transformedRoutes = tripDetail?.routes?.map(route => ({
      route: route.route || null,
      startStop: route.startStop ? {
        stopOrigin: route.startStop,
        stopDestination: null
      } : null,
      endStop: route.endStop ? {
        stopOrigin: null,
        stopDestination: route.endStop
      } : null
    })) || [{ route: null, startStop: null, endStop: null }];

    reset({
      ...INITIAL_STATE,
      ...tripDetail,
      vehicle: tripDetail?.vehicle || null,
      driver: tripDetail?.driver || null,
      isActive: tripDetail?.isActive ?? true,
      sourceBranch: tripDetail?.sourceBranch || null,
      destinationBranch: tripDetail?.destinationBranch || null,
      routes: transformedRoutes,
    });
  }, [tripDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id || viewEntity?.entity?.account,
      });
    }
  }, [isAccountAdmin, isSystemAdmin, _watchAccount?._id, fetchEntities, viewEntity?.entity?.account]);

  useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchBranches({ account: _watchAccount?._id, entity: _watchEntity._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches, viewEntity]);

  useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchRoutes({ account: _watchAccount?._id, entity: _watchEntity._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchRoutes, viewEntity]);

  React.useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchDrivers({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        active: "true",
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchDrivers]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Account"
                    required
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("entity", null);
                      setValue("vehicle", null);
                      setValue("driver", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicle", null);
                      setValue("driver", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="driver"
              control={control}
              rules={{ required: "Please select a driver." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Driver"
                  value={field.value || null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    // Only reset vehicle if the new driver doesn't have the currently selected vehicle
                    if (!data?.assigned_vehicle_ids?.some(v => v._id === _watchVehicle?._id)) {
                      setValue("vehicle", null);
                    }
                  }}
                  getOptionLabel={(option) => (option ? `${option.name} - ${option.driverId}` : "")}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={drivers ? Object.values(drivers) : []}
                  loading={loadingDrivers}
                  loadingText="Loading Drivers..."
                  helperText={errors?.driver?.message}
                  error={!!errors?.driver}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>

            <Controller
              name="vehicle"
              control={control}
              rules={{ required: "Please select a vehicle." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Vehicle"
                  value={field.value || null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    // Additional logic if needed when vehicle changes
                  }}
                  getOptionLabel={(option) => (option ? `${option.vehicle_id} - ${option.registration_number}` : "")}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={selectedVehicle}
                  disabled={!_watchDriver}
                  loading={loadingDrivers}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicle?.message}
                  error={!!errors?.vehicle}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="sourceBranch"
              control={control}
              rules={{ required: "Please select source branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Source Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("routes", [{ route: null, startStop: null, endStop: null }]);
                  }}
                  getOptionLabel={(option) => (option ? `${option?.name} - ${option?.code}` : "")}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading source branches..."
                  helperText={errors?.sourceBranch?.message}
                  error={!!errors?.sourceBranch}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="destinationBranch"
              control={control}
              rules={{ required: "Please select destination branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Destination Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("routes", [{ route: null, startStop: null, endStop: null }]);
                  }}
                  getOptionLabel={(option) => (option ? `${option?.name} - ${option?.code}` : "")}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading destination branches..."
                  helperText={errors?.destinationBranch?.message}
                  error={!!errors?.destinationBranch}
                />
              )}
            />
          </Grid>

          {_watchRoutes?.map((routeObj, index) => {
            // Filter routes based on previous selection
            const filteredRoutes = (() => {
              if (!routes) return [];

              const allRoutes = Object.values(routes);

              // First route must include source branch
              if (index === 0 && _watchSourceBranch?.code) {
                return allRoutes.filter(route =>
                  route.routeStops?.some(stop =>
                    stop.stopOrigin?.code === _watchSourceBranch.code ||
                    stop.stopDestination?.code === _watchSourceBranch.code
                  )
                );
              }

              // Subsequent routes must start from previous end stop
              if (index > 0) {
                const prevEndStop = _watchRoutes[index - 1]?.endStop?.stopDestination?.code;
                if (!prevEndStop) return [];

                return allRoutes.filter(route =>
                  route.routeStops?.some(stop =>
                    stop.stopOrigin?.code === prevEndStop
                  )
                );
              }

              return allRoutes;
            })();

            // Get all possible start stops
            const getAllStartStops = () => {
              if (!routeObj?.route?.routeStops) {
                // Handle case when editing existing trip
                if (routeObj?.startStop?.stopOrigin) {
                  return [{
                    ...routeObj.startStop,
                    stopOrigin: routeObj.startStop.stopOrigin,
                    isOrigin: true
                  }];
                }
                return [];
              }

              const stops = [];

              // First route must start with source branch
              if (index === 0 && _watchSourceBranch?.code) {
                routeObj.route.routeStops.forEach(stop => {
                  if (stop.stopOrigin?.code === _watchSourceBranch.code) {
                    stops.push({
                      ...stop,
                      stopOrigin: stop.stopOrigin,
                      isOrigin: true
                    });
                  }
                  if (stop.stopDestination?.code === _watchSourceBranch.code) {
                    stops.push({
                      ...stop,
                      stopOrigin: stop.stopDestination,
                      isDestinationAsOrigin: true
                    });
                  }
                });
                return stops;
              }

              // For other routes, show all possible starts
              routeObj.route.routeStops.forEach(stop => {
                stops.push({
                  ...stop,
                  stopOrigin: stop.stopOrigin,
                  isOrigin: true
                });
              });

              return stops;
            };

            // Get ALL possible end stops (no filtering)
            const getAllEndStops = () => {
              if (!routeObj?.route?.routeStops) {
                // Handle case when editing existing trip
                if (routeObj?.endStop?.stopDestination) {
                  return [{
                    ...routeObj.endStop,
                    stopDestination: routeObj.endStop.stopDestination,
                    isDestination: true
                  }];
                }
                return [];
              }

              const stopsMap = new Map();

              // Add all destinations
              routeObj.route.routeStops.forEach(stop => {
                if (stop.stopDestination) {
                  stopsMap.set(stop.stopDestination._id, {
                    ...stop,
                    stopDestination: stop.stopDestination,
                    isDestination: true
                  });
                }
              });

              // Add all origins as possible ends (without duplicates)
              routeObj.route.routeStops.forEach(stop => {
                if (stop.stopOrigin && !stopsMap.has(stop.stopOrigin._id)) {
                  stopsMap.set(stop.stopOrigin._id, {
                    ...stop,
                    stopDestination: stop.stopOrigin,
                    isOriginAsDestination: true
                  });
                }
              });

              return Array.from(stopsMap.values());
            };


            return (
              <Grid container item xs={12} spacing={2} key={index}>
                {/* Route selection remains same */}
                <Grid item xs={4}>
                  <Controller
                    name={`routes[${index}].route`}
                    control={control}
                    rules={{ required: "Please select a route." }}
                    render={({ field: { value, onChange } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label={`Select Route ${index + 1}`}
                        required
                        value={value ?? null}
                        onChange={(_, data) => {
                          onChange(data);
                          setValue(`routes[${index}].startStop`, null);
                          setValue(`routes[${index}].endStop`, null);
                          if (index < _watchRoutes.length - 1 && data) {
                            const newRoutes = _watchRoutes.map((r, i) =>
                              i <= index ? r : { route: null, startStop: null, endStop: null }
                            );
                            setValue("routes", newRoutes);
                          }
                        }}
                        getOptionLabel={(option) => option?.routeName || ""}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        options={filteredRoutes}
                        loading={loadingRoutes}
                        loadingText="Loading routes..."
                        helperText={errors?.routes?.[index]?.route?.message}
                        error={!!errors?.routes?.[index]?.route}
                      />
                    )}
                  />
                </Grid>

                {/* Start Stop */}
                <Grid item xs={4}>
                  <Controller
                    name={`routes[${index}].startStop`}
                    control={control}
                    rules={{ required: "Please select a start stop." }}
                    render={({ field: { value, onChange } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label={`Start Stop for Route ${index + 1}`}
                        required
                        value={value ?? null}
                        onChange={(_, data) => {
                          onChange(data);
                          // Only clear end stop if route changed
                          if (value?.stopOrigin?.code !== data?.stopOrigin?.code) {
                            setValue(`routes[${index}].endStop`, null);
                          }
                        }}
                        getOptionLabel={(option) => option ? `${option.stopOrigin?.code}` : ""}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        options={getAllStartStops()}
                        disabled={!routeObj?.route}
                        loading={loadingRoutes}
                        loadingText="Loading stops..."
                        helperText={errors?.routes?.[index]?.startStop?.message}
                        error={!!errors?.routes?.[index]?.startStop}
                      />
                    )}
                  />
                </Grid>

                {/* End Stop - Now shows ALL options */}
                <Grid item xs={4}>
                  <Controller
                    name={`routes[${index}].endStop`}
                    control={control}
                    rules={{
                      required: "Please select an end stop.",
                      // No inline validation here - will be handled on submit
                    }}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label={`End Stop for Route ${index + 1}`}
                        required
                        value={value ?? null}
                        onChange={(_, data) => onChange(data)}
                        onBlur={onBlur}
                        getOptionLabel={(option) => option ? `${option.stopDestination?.code}` : ""}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        options={getAllEndStops()}
                        disabled={!routeObj?.route}
                        loading={loadingRoutes}
                        loadingText="Loading stops..."
                        helperText={errors?.routes?.[index]?.endStop?.message}
                        error={!!errors?.routes?.[index]?.endStop}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            );
          })}
          <Grid item xs={12}>
            <Button
              startIcon={<AddIcon />}
              onClick={addRouteField}
              color="primary"
              sx={{ textTransform: "none", marginRight: 2 }}
            >
              Add Route
            </Button>
            {_watchRoutes && _watchRoutes.length > 1 && (
              <Button
                startIcon={<RemoveIcon />}
                onClick={removeRouteField}
                color="secondary"
                sx={{ textTransform: "none" }}
              >
                Remove Route
              </Button>
            )}
          </Grid>
          {tripDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => <AppSwitch {...field} label="Active Status" />}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {tripDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default TripForm;